import React, { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Player } from '../types';
import { Trophy } from 'lucide-react';

interface WinnerModalProps {
  winner: Player;
  /** 传了对手才显示最终比分 */
  opponent?: Player;
  onRestart: () => void;
  onHome: () => void;
}

interface WinnerTheme {
  ring: string;
  iconWrap: string;
  icon: string;
  name: string;
  primaryButton: string;
  glow: string;
  confettiColors: string[];
}

// 主题写成常量表：Tailwind 只会在源码里扫到完整类名时才生成样式。
// `bg-${color}-500` 这种拼接写法只是碰巧被别的文件兜住——`.to-blue-500` 一度只存在于
// ios/App/App/public/assets 里的旧构建产物，产物一重新生成就会静默失效。
const WINNER_THEMES: Record<string, WinnerTheme> = {
  red: {
    ring: 'bg-gradient-to-br from-red-500 via-white to-red-500',
    iconWrap: 'border-red-500 bg-red-500/20',
    icon: 'text-red-500',
    name: 'text-red-400',
    primaryButton: 'border-red-500 text-red-500 hover:bg-red-500 hover:text-black',
    glow: 'neon-glow-red',
    confettiColors: ['#ef4444', '#ffffff'],
  },
  blue: {
    ring: 'bg-gradient-to-br from-blue-500 via-white to-blue-500',
    iconWrap: 'border-blue-500 bg-blue-500/20',
    icon: 'text-blue-500',
    name: 'text-blue-400',
    primaryButton: 'border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-black',
    glow: 'neon-glow-blue',
    confettiColors: ['#3b82f6', '#ffffff'],
  },
};

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

// 礼花的形状：开场一次重击，随后两侧递减地补，2.2 秒收干净。
const CONFETTI_TOTAL_MS = 2200;
const CONFETTI_BURST_MS = 140;

const WinnerModal: React.FC<WinnerModalProps> = ({ winner, opponent, onRestart, onHome }) => {
  const theme = WINNER_THEMES[winner.color] ?? WINNER_THEMES.red;
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const celebrationFiredRef = useRef(false);

  const shouldReduceMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }, []);

  useEffect(() => {
    const colors = theme.confettiColors;

    // 开场那一下：StrictMode 在开发环境会挂载两次，所以用 ref 保证只放一次
    if (!celebrationFiredRef.current) {
      celebrationFiredRef.current = true;
      confetti(
        shouldReduceMotion
          ? { particleCount: 70, spread: 85, origin: { y: 0.62 }, colors }
          : { particleCount: 90, spread: 78, origin: { y: 0.62 }, colors }
      );
    }

    // 减少动态不等于零动态：保留上面这一次静态礼花，去掉持续轰炸
    if (shouldReduceMotion) return;

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const progress = Math.min(1, (Date.now() - startedAt) / CONFETTI_TOTAL_MS);
      const particleCount = Math.max(1, Math.round(5 * (1 - progress)));
      confetti({ particleCount, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (progress >= 1) window.clearInterval(timer);
    }, CONFETTI_BURST_MS);

    // 点 Home / New Game 会立刻卸载这个窗口，不清掉定时器的话礼花会一路炸到首页上
    return () => window.clearInterval(timer);
  }, [theme, shouldReduceMotion]);

  // 焦点落进弹窗，Esc 等同于返回首页
  useEffect(() => {
    primaryButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onHome();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onHome]);

  const titleId = 'winner-modal-title';

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        initial={shouldReduceMotion ? { opacity: 0 } : { scale: 0.95, opacity: 0 }}
        animate={shouldReduceMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
        exit={
          shouldReduceMotion
            ? { opacity: 0, transition: { duration: 0.12, ease: EASE_OUT } }
            : { scale: 0.97, opacity: 0, transition: { duration: 0.16, ease: EASE_OUT } }
        }
        transition={{ type: 'spring', duration: 0.45, bounce: 0.18 }}
        className={`relative w-full max-w-sm rounded-2xl p-1 ${theme.ring}`}
      >
        <div className="flex w-full flex-col items-center rounded-xl bg-black p-6 text-center md:p-8">
          <div className={`mb-4 rounded-full border-2 p-3 ${theme.iconWrap}`}>
            <Trophy size={48} className={theme.icon} aria-hidden="true" />
          </div>

          <h2 id={titleId} className={`mb-1 text-3xl font-orbitron font-bold uppercase text-white md:text-4xl ${theme.glow}`}>
            VICTORY! / 胜利
          </h2>

          <p className="font-rajdhani text-base tracking-wider text-neutral-300 md:text-lg">
            <span className={`text-lg font-bold md:text-xl ${theme.name}`}>{winner.name}</span>{' '}
            has won the battle! / 拿下这场比赛
          </p>

          {opponent && (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-1.5">
              <span className="font-orbitron text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Final Score / 最终分数
              </span>
              <span className="font-orbitron text-xl font-bold text-white">
                {winner.health}
                <span className="px-1 text-zinc-600">–</span>
                {opponent.health}
              </span>
            </div>
          )}

          <button
            ref={primaryButtonRef}
            onClick={onRestart}
            className={`mt-6 w-full rounded-xl border-2 px-6 py-2.5 text-sm font-orbitron font-bold uppercase tracking-widest transition-[background-color,color,border-color,transform] duration-150 active:scale-[0.97] ${theme.primaryButton}`}
          >
            开始新对局 / New Game
          </button>

          <button
            onClick={onHome}
            className="mt-2.5 w-full rounded-xl border-2 border-white/25 px-6 py-2.5 text-sm font-orbitron font-bold uppercase tracking-widest text-white/70 transition-[background-color,color,border-color,transform] duration-150 hover:bg-white/10 hover:text-white active:scale-[0.97]"
          >
            Home / 返回首页
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WinnerModal;
